/**
 * Shared contract + helpers for the generic bulk search results surface.
 *
 * A BulkSearchDescriptor adapts one entity (payment / artifact / execution) to
 * the shared BulkSearchResults component: it supplies the table columns, the
 * server-side PropertyFilter properties, how to build + dispatch the search for
 * a page of results, and how to read the result back out of the store. All the
 * UI/pagination/filter plumbing lives once in BulkSearchResults.
 */
import { ReactNode } from 'react';
import Link from '@cloudscape-design/components/link';
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import { TableProps } from '@cloudscape-design/components/table';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { useNavigate } from 'react-router-dom';
import { toFlowState } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { AppDispatch, RootState } from 'src/store/store';

/** Args for one page of a bulk search: the queried ids + pagination + filter tokens. */
export interface BulkSearchArgs {
  ids: string[];
  pageNumber: number;
  pageSize: number;
  /** PropertyFilter tokens grouped by property key (equals operator only). */
  filters: Record<string, string[]>;
}

/** Server result projection the component renders from. */
export interface BulkSearchResultState<TRow> {
  items: TRow[];
  total: number;
  loading: boolean;
  error: string | null;
}

/** Per-entity adapter consumed by BulkSearchResults. */
export interface BulkSearchDescriptor<TRow> {
  /** Lowercase entity noun, e.g. "payment". */
  entity: string;
  /** Page/results heading, e.g. "Bulk payment search". */
  title: string;
  /** Route path of this results page, e.g. "/payments" (breadcrumb). */
  path: string;
  columns: TableProps.ColumnDefinition<TRow>[];
  /** Server-side filterable properties (status/state/type). */
  filteringProperties: PropertyFilterProps.FilteringProperty[];
  /** Dispatch the search for the given page/filters. */
  run: (dispatch: AppDispatch, args: BulkSearchArgs) => void;
  /** Clear server state (called on unmount). */
  clear: (dispatch: AppDispatch) => void;
  /** Read the result projection out of the store. */
  select: (state: RootState) => BulkSearchResultState<TRow>;
}

/** Reduces PropertyFilter tokens to `{ propertyKey: [values] }` (equals, non-empty). */
export const queryToFilterMap = (query: PropertyFilterProps.Query): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  for (const token of query.tokens) {
    if (typeof token.propertyKey !== 'string' || typeof token.value !== 'string') continue;
    const value = token.value.trim();
    if (token.operator !== '=' || !value) continue;
    (out[token.propertyKey] = out[token.propertyKey] ?? []).push(value);
  }
  return out;
};

/** First value of a filter key, or undefined — for single-valued API fields. */
export const firstOf = (filters: Record<string, string[]>, key: string): string | undefined =>
  filters[key]?.[0];

/** Display a string, or "-" when empty / the literal "null". */
export const dash = (value?: string | null): string => (value && value !== 'null' ? value : '-');

/** SPA-routed link used in id columns (keeps Redux state, no full reload). */
export const DetailLink = ({ to, children }: { to: string; children: ReactNode }) => {
  const navigate = useNavigate();
  return (
    <Link href={to} onFollow={(e) => { e.preventDefault(); navigate(to); }}>{children}</Link>
  );
};

/** Beacon state → StatusIndicator type (SUCCESS/FAILED/in-progress). */
export const flowStatusType = (state?: string): StatusIndicatorProps.Type => {
  const flow = toFlowState(state);
  return flow === 'SUCCESS' ? 'success' : flow === 'FAILED' ? 'error' : 'in-progress';
};

/** Status cell: colored indicator (by state) showing the status label. */
export const StatusCell = ({ state, label }: { state?: string; label?: string }) => (
  <StatusIndicator type={flowStatusType(state)}>{dash(label)}</StatusIndicator>
);
